import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Blog · Careable 360+ Plus";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/blog");
        if (!cancelled) setPosts(data || []);
      } catch (err) {
        if (!cancelled)
          setError(err?.response?.data?.detail || err.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="blog-list-page"
      className="min-h-screen bg-[#F4FAF8] text-[#0E2A36] flex flex-col"
    >
      <header className="bg-[#1F4E5F] text-[#F4FAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link
            data-testid="blog-back-home"
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#F4FAF8]/75 hover:text-[#F4FAF8] mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="text-[11px] font-bold tracking-[0.22em] text-[#E8A33D] uppercase">
            From the team
          </p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 leading-tight">
            The Careable Blog
          </h1>
          <p className="mt-4 text-[#F4FAF8]/80 max-w-2xl text-[15px] leading-relaxed">
            Stories, guidance, and updates on senior care, health coordination,
            and building Careable 360+ Plus for families across India.
          </p>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading stories…
          </div>
        ) : error ? (
          <div data-testid="blog-error" className="py-16 text-center text-[#C84444] text-sm">
            {error}
          </div>
        ) : posts.length === 0 ? (
          <div data-testid="blog-empty" className="py-20 text-center max-w-md mx-auto">
            <p className="font-display text-xl font-bold text-[#0E2A36]">
              No posts yet
            </p>
            <p className="text-sm text-[#6A8290] mt-2">
              We're working on our first stories. Check back soon — or join the
              beta to be among the first to read them.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 bg-[#1F4E5F] text-[#F4FAF8] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#173E50]"
            >
              Back to home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div
            data-testid="blog-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {posts.map((p) => (
              <Link
                key={p.id}
                data-testid={`blog-card-${p.slug}`}
                to={`/blog/${p.slug}`}
                className="group bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden hover:shadow-[0_8px_30px_rgba(15,56,32,0.08)] transition-all hover:-translate-y-0.5 flex flex-col"
              >
                {p.cover_image_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-[#E1EEEA]">
                    <img
                      src={p.cover_image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#1F4E5F] to-[#2C6E83]" />
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="font-display text-lg font-bold text-[#0E2A36] leading-snug group-hover:text-[#1F4E5F]">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-[#3D5560] leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-4 pt-3 border-t border-[#E1EEEA] flex items-center gap-3 text-[11px] text-[#6A8290]">
                    {p.author && (
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" /> {p.author}
                      </span>
                    )}
                    {p.published_at && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {fmtDate(p.published_at)}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
